import {
  NegotiableFieldOptional,
  NegotiableFieldRequired,
  Negotiation,
  NegotiationFieldMeta,
  NegotiationOverride,
  NegotiationOverrideUpdate,
  NegotiationSide,
  NegotiationType,
  NegotiationUpdate,
  OverriddenNegotiationSide
} from 'api'
import { isNil, isUndefined } from 'lodash'
import { RepositoryEntry } from 'store/repository'

export const negotiationSideToOverridenNegotiationSide = (s: NegotiationSide) =>
  s === NegotiationSide.INITIATOR ? OverriddenNegotiationSide.INITIATOR : OverriddenNegotiationSide.RESPONDER

export const negotiationOverrideUpdateVersionMatch = (
  { entity: negotiation }: NegotiationUpdate,
  { entity: { overriddenVersionParent, overriddenVersionSide } }: NegotiationOverrideUpdate
): boolean => negotiation.version === `${overriddenVersionParent}.${overriddenVersionSide}`

const createUpdateNegotiableField = (side?: NegotiationSide) => <V, T extends NegotiableFieldOptional<V> | NegotiableFieldRequired<V>>(
  negotiableField: T,
  value: V
): T => {
  const updateModified = isNil(side)
  return {
    ...negotiableField,
    ...(side === NegotiationSide.RESPONDER && { responderLastValue: value }),
    ...(side === NegotiationSide.INITIATOR && { initiatorLastValue: value }),
    ...(!updateModified && { negotiated: value }),
    modifiedValue: updateModified ? value : null,
    modified: updateModified ? true : false
  }
}

const NON_NEGOTIABLE_FIELDS_TO_UPDATE: [keyof Negotiation, NegotiationFieldMeta][] = [
  ['accruedInterest', NegotiationFieldMeta.ACCRUED_INTEREST],
  ['repoInterest', NegotiationFieldMeta.REPO_INTEREST],
  ['dirtyPrice', NegotiationFieldMeta.DIRTY_PRICE],
  ['cleanPrice', NegotiationFieldMeta.CLEAN_PRICE],
  ['allInPrice', NegotiationFieldMeta.ALL_IN_PRICE],
  ['endConsideration', NegotiationFieldMeta.END_CONSIDERATION],
  ['calculatedStartConsideration', NegotiationFieldMeta.CALCULATED_START_CONSIDERATION],
  ['absoluteConsideration', NegotiationFieldMeta.ABSOLUTE_START_CONSIDERATION]
]
export const NEGOTIABLE_FIELDS_TO_UPDATE: [keyof Negotiation, NegotiationFieldMeta][] = [
  ['rate', NegotiationFieldMeta.RATE],
  ['haircut', NegotiationFieldMeta.HAIRCUT],
  ['haircutType', NegotiationFieldMeta.HAIRCUT_TYPE],
  ['bondPrice', NegotiationFieldMeta.BOND_PRICE],
  ['notional', NegotiationFieldMeta.NOTIONAL],
  ['startConsideration', NegotiationFieldMeta.START_CONSIDERATION],
  ['initiatorExecutionDecisionMaker', NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER],
  ['initiatorInvestmentDecisionMaker', NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER],
  ['responderExecutionDecisionMaker', NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER],
  ['responderInvestmentDecisionMaker', NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER]
]

const applyNegotiationOverrideToNegotiation = (
  negotiation: Negotiation,
  { updatedValues, overriddenBySide, overriddenSide, userIdWithLock, modified, negotiationStatus }: NegotiationOverride
): Negotiation => {
  const updateNegotiableField = createUpdateNegotiableField(
    overriddenSide === OverriddenNegotiationSide.BOTH ? overriddenBySide : undefined
  )

  const negotiableUpdates = NEGOTIABLE_FIELDS_TO_UPDATE.reduce(
    (updates, [localField, updateField]) =>
      !isUndefined(updatedValues[updateField])
        ? { ...updates, [localField]: updateNegotiableField(negotiation[localField] as any, updatedValues[updateField]) }
        : updates,
    {}
  )

  const nonNegotiableUpdates = NON_NEGOTIABLE_FIELDS_TO_UPDATE.reduce(
    (updates, [localField, updateField]) =>
      !isUndefined(updatedValues[updateField])
        ? {
            ...updates,
            [localField]: updatedValues[updateField]
          }
        : updates,
    {}
  )

  const isNegotiationTypeSummary = [NegotiationType.CONTINGENT_SUMMARY, NegotiationType.ONE_CANCELS_OTHERS_SUMMARY].includes(
    negotiation.negotiationType
  )

  return {
    ...negotiation,
    ...(isNegotiationTypeSummary && {
      summaryModified: modified
    }),
    ...negotiableUpdates,
    ...nonNegotiableUpdates,
    userIdWithLock,
    status: negotiationStatus
  }
}

export const applyNegotiationOverrideToRepositoryEntry = (
  entry: RepositoryEntry<NegotiationUpdate>,
  override: NegotiationOverrideUpdate
) => ({
  ...entry,
  lastUpdate: {
    ...entry.lastUpdate,
    entity: applyNegotiationOverrideToNegotiation(entry.lastUpdate.entity, override.entity),
    initiatorAvailableEvents: override.initiatorAvailableEvents,
    responderWithoutLockAvailableEvents: override.responderWithoutLockAvailableEvents,
    responderWithLockAvailableEvents: override.responderWithLockAvailableEvents,
    validationIssues: override.validationIssues
  }
})
